import React, { useEffect, useRef, useState } from 'react';
import SectionIntro from './SectionIntro';
import { VISUALS } from './showcase/StageVisuals';

const stages = [
  {
    title: 'Orders',
    desc: 'Every order captured across the floor, the counter, online, and delivery partners.',
    visual: 'orders',
  },
  {
    title: 'Preparation',
    desc: 'Work routes to the right station with live timing, so nothing sits waiting.',
    visual: 'preparation',
  },
  {
    title: 'Inventory',
    desc: 'Stock depletes in real time as items sell, and reorders draft themselves.',
    visual: 'inventory',
  },
  {
    title: 'Storefront',
    desc: 'A commission-free online store selling the same catalogue, on the same stock.',
    visual: 'storefront',
  },
  {
    title: 'Fulfilment',
    desc: 'Every order tracked from payment to doorstep, whoever is doing the delivering.',
    visual: 'fulfilment',
  },
  {
    title: 'Customers',
    desc: 'One record per customer — history, spend, and loyalty, across every channel.',
    visual: 'customers',
  },
  {
    title: 'Staff',
    desc: 'Shifts, roles, and permissions in one place, so people see exactly what they should.',
    visual: 'staff',
  },
  {
    title: 'Branches',
    desc: 'One operating standard rolled out to every location, and compared side by side.',
    visual: 'branches',
  },
  {
    title: 'Accounting',
    desc: 'Revenue, cost, and margin reconcile as they happen — not at month end.',
    visual: 'accounting',
  },
  {
    title: 'Analytics',
    desc: 'Live dashboards show what is working across every branch and channel.',
    visual: 'analytics',
  },
  {
    title: 'AI Insights',
    desc: 'Forecast demand, flag waste, and act on the next best decision before it costs you.',
    visual: 'ai',
  },
];

const StageVisual: React.FC<{ type: string; active: boolean }> = ({ type, active }) => {
  const Visual = VISUALS[type];

  return (
    <div
      className={`device-frame w-full max-w-lg mx-auto transition-all duration-700 ${
        active ? 'opacity-100 scale-100' : 'opacity-40 scale-95'
      }`}
    >
      <div className="device-chrome">
        <span /><span /><span />
      </div>
      <div className="pt-12 px-5 pb-5 sm:px-6 sm:pb-6 min-h-[300px] bg-surface-secondary">
        {Visual ? <Visual active={active} /> : null}
      </div>
    </div>
  );
};

const ProductShowcase: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const total = containerRef.current.offsetHeight - window.innerHeight;
      if (total <= 0) return;
      const progress = Math.min(1, Math.max(0, -rect.top / total));
      const index = Math.min(stages.length - 1, Math.floor(progress * stages.length));
      setActiveIndex(index);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section className="relative bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 md:pt-32">
        <SectionIntro
          align="center"
          label="Featured Product Showcase"
          title="From order to insight — one continuous system."
          body="Scroll to follow how operations and commerce connect inside a Nexasolutra system."
          className="mb-8 md:mb-12"
        />
      </div>

      {/* Desktop sticky scroll story */}
      <div ref={containerRef} className="hidden md:block relative" style={{ height: `${stages.length * 55}vh` }}>
        <div className="sticky top-0 h-screen flex items-center">
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 grid grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="space-y-0 mb-6">
                {stages.map((stage, i) => (
                  <button
                    key={stage.title}
                    type="button"
                    onClick={() => {
                      if (!containerRef.current) return;
                      const total = containerRef.current.offsetHeight - window.innerHeight;
                      // offsetTop is measured against the nearest positioned ancestor —
                      // this section — so it is not a document coordinate. Derive one.
                      const containerTop =
                        containerRef.current.getBoundingClientRect().top + window.scrollY;
                      // Aim at the middle of the stage's band so rounding cannot land short.
                      const target = containerTop + ((i + 0.5) / stages.length) * total;
                      window.scrollTo({ top: target, behavior: 'smooth' });
                    }}
                    className={`flex items-center gap-4 w-full text-left py-2 transition-all duration-500 ${
                      i === activeIndex ? 'opacity-100' : 'opacity-35 hover:opacity-60'
                    }`}
                  >
                    <span
                      className={`h-px transition-all duration-500 ${
                        i === activeIndex ? 'bg-primary-500 w-12' : 'bg-surface-border w-8'
                      }`}
                    />
                    <span
                      className={`font-display text-lg lg:text-xl font-semibold ${
                        i === activeIndex ? 'text-white' : 'text-muted'
                      }`}
                    >
                      {stage.title}
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-muted text-base lg:text-lg leading-relaxed max-w-md transition-opacity duration-500">
                {stages[activeIndex].desc}
              </p>
            </div>
            <StageVisual type={stages[activeIndex].visual} active />
          </div>
        </div>
      </div>

      {/* Mobile stacked */}
      <div className="md:hidden max-w-7xl mx-auto px-4 pb-24 space-y-12">
        {stages.map((stage) => (
          <div key={stage.title}>
            <h3 className="font-display text-2xl font-semibold text-white mb-2">{stage.title}</h3>
            <p className="text-muted mb-6">{stage.desc}</p>
            <StageVisual type={stage.visual} active />
          </div>
        ))}
      </div>
    </section>
  );
};

export default ProductShowcase;
